// Assistant passkey logins (docs/ASSISTANT-LOGIN-SPEC.md) — the client's side of
// src/assistant.js. Public calls (sign-in, phone enrolment) carry NO bearer
// token: there is no session yet. Management calls ride authedGet/authedWrite.
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON, PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { API_BASE } from './config';
import { authedGet, authedWrite } from './apiClient';

export class AssistantError extends Error {
  /** 'cancelled' | 'not-set-up' | 'disabled' | 'expired' | 'busy' | 'other' */
  kind: string;
  constructor(message: string, kind = 'other') {
    super(message);
    this.name = 'AssistantError';
    this.kind = kind;
  }
}

async function publicPost<T>(path: string, body: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    throw new AssistantError('Could not reach nvManager. Check the connection and try again.', 'other');
  }
  const parsed = await res.json().catch(() => ({})) as { error?: string };
  if (!res.ok) {
    const kind = res.status === 410 ? 'expired' : res.status === 429 ? 'busy' : res.status === 401 ? 'not-set-up' : 'other';
    throw new AssistantError(parsed.error || `HTTP ${res.status}`, kind);
  }
  return parsed as T;
}

/** What the browser says when the person closes or declines the passkey prompt. */
function isCancel(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name;
  return name === 'NotAllowedError' || name === 'AbortError';
}

export interface AssistantSignIn {
  sessionToken: string;
  assistant: { id: number; displayName: string; locationCode: string };
}

/** Passkey sign-in. Resolves with the app session token. */
export async function assistantSignIn(): Promise<AssistantSignIn> {
  const { options } = await publicPost<{ options: PublicKeyCredentialRequestOptionsJSON }>(
    '/api/assistant/signin/options', {},
  );
  let response;
  try {
    response = await startAuthentication({ optionsJSON: options });
  } catch (err) {
    if (isCancel(err)) throw new AssistantError('Sign-in was cancelled. Choose Sign in as assistant to try again.', 'cancelled');
    throw new AssistantError('This device could not use a passkey. Try again or ask a manager.', 'other');
  }
  try {
    return await publicPost<AssistantSignIn>('/api/assistant/signin/verify', { response });
  } catch (err) {
    if (err instanceof AssistantError && err.kind === 'not-set-up') {
      throw new AssistantError(
        'That phone is not set up, or the assistant was disabled. Ask a manager for a new setup code.', 'not-set-up',
      );
    }
    throw err;
  }
}

/** Sign in with a Google Authenticator code. Resolves with the app session token. */
export async function assistantCodeSignIn(username: string, code: string): Promise<AssistantSignIn> {
  return publicPost<AssistantSignIn>('/api/assistant/code/signin', { username, code });
}

export interface TotpSetup {
  display_name: string; username: string; secret: string; otpauth: string; sealed: string;
}

export function totpOptions(token: string): Promise<TotpSetup> {
  return publicPost('/api/assistant/totp/options', { token });
}

export function totpEnrol(token: string, sealed: string, code: string): Promise<{ enrolled: boolean; username: string }> {
  return publicPost('/api/assistant/totp/enrol', { token, sealed, code });
}

export async function enrolOptions(token: string): Promise<{
  options: PublicKeyCredentialCreationOptionsJSON; display_name: string; location_code?: string;
}> {
  return publicPost('/api/assistant/enrol/options', { token });
}

/** Create the passkey on this phone and send it to the server. */
export async function enrolPhone(
  token: string, options: PublicKeyCredentialCreationOptionsJSON,
): Promise<{ enrolled: boolean; display_name: string }> {
  let response;
  try {
    response = await startRegistration({ optionsJSON: options });
  } catch (err) {
    if (isCancel(err)) throw new AssistantError('Set-up was cancelled. Tap the button to try again.', 'cancelled');
    throw new AssistantError('This phone could not create a passkey. Make sure a screen lock is on, then try again.', 'other');
  }
  return publicPost('/api/assistant/enrol/verify', { token, response });
}

// --- management (owner / director / manager) ---------------------------------

export interface AssistantRow {
  id: number;
  display_name: string;
  username?: string;
  totp_set?: boolean;
  location_code: string;
  active: boolean;
  created_by?: string | null;
  created_at?: string | null;
  disabled_by?: string | null;
  disabled_at?: string | null;
  passkeys: Array<{ credential_id: string; created_at: string | null; last_used_at: string | null }>;
  link_expires_at: string | null;
}

export function listAssistants(): Promise<{ assistants: AssistantRow[] }> {
  return authedGet('/api/v1/assistants');
}

export function createAssistant(displayName: string, locationCode?: string): Promise<{
  assistant: { id: number; display_name: string; location_code: string; active: boolean };
  enrolment_url: string; expires_at: string;
}> {
  return authedWrite('POST', '/api/v1/assistants', {
    display_name: displayName,
    ...(locationCode ? { location_code: locationCode } : {}),
  });
}

export function newEnrolmentLink(id: number): Promise<{ enrolment_url: string; expires_at: string }> {
  return authedWrite('POST', `/api/v1/assistants/${id}/enrolment`, {});
}

export function resetAuthenticator(id: number): Promise<{ enrolment_url: string; expires_at: string }> {
  return authedWrite('POST', `/api/v1/assistants/${id}/totp/reset`, {});
}

export function disableAssistant(id: number): Promise<unknown> {
  return authedWrite('POST', `/api/v1/assistants/${id}/disable`, {});
}

export function removeAssistantPasskey(id: number, credentialId: string): Promise<unknown> {
  return authedWrite('POST', `/api/v1/assistants/${id}/passkeys/remove`, { credential_id: credentialId });
}
